<template>
  <v-dialog
    v-model="dialog"
    max-width="800px"
    persistent
    scrollable
    :fullscreen="$vuetify.breakpoint.xs"
  >
    <v-card>
      <!-- Modal Header -->
      <v-card-title
        class="headline d-flex justify-space-between align-center pa-4 white--text"
        :style="{ backgroundColor: getHeaderBackgroundColor() }"
      >
        <div class="d-flex align-center">
          <v-icon left color="white" class="mr-2">mdi-car</v-icon>
          <span class="text-h5 font-weight-bold">Vehicle Details</span>
        </div>
        <v-btn icon @click="closeModal" class="close-btn">
          <v-icon color="white" class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Vehicle Content -->
      <v-card-text v-if="vehicle" class="pa-0">
        <div class="vehicle-detail-container">
          <!-- Vehicle Header with Color -->
          <div class="vehicle-header pa-4" :style="{ backgroundColor: getHeaderBackgroundColor() }">
            <div class="text-center">
              <h2 class="text-h4 font-weight-bold white--text mb-2">
                {{ getVehicleName() }}
              </h2>
              <div class="text-h3 font-weight-bold white--text">
                {{ currencyFormat(vehicle.amount) }}
              </div>
              <v-chip
                v-if="vehicle.is_external"
                color="white"
                :text-color="columnColor"
                class="mt-2"
              >
                <v-icon left small>mdi-robot</v-icon>
                Found by SyncGuideX on {{ vehicle.site_name || vehicle.source }}
              </v-chip>
            </div>
          </div>

          <!-- Vehicle Images -->
          <div class="vehicle-images pa-4">
            <v-carousel
              v-if="vehicle.images && vehicle.images.length > 0"
              height="400"
              hide-delimiters
              show-arrows-on-hover
            >
              <v-carousel-item
                v-for="(image, i) in vehicle.images"
                :key="i"
              >
                <v-img
                  height="400"
                  :src="image.image || getVehicleImage()"
                  @error="handleImageError"
                  contain
                ></v-img>
              </v-carousel-item>
            </v-carousel>
            <v-img
              v-else
              height="400"
              :src="getVehicleImage()"
              @error="handleImageError"
              contain
            ></v-img>
          </div>

          <v-divider></v-divider>

          <!-- Vehicle Information -->
          <div class="vehicle-info pa-4">
            <v-row>
              <!-- Basic Information -->
              <v-col cols="12" md="6">
                <h3 class="text-h6 font-weight-bold mb-3" :style="{ color: columnColor }">
                  <v-icon left :color="columnColor">mdi-information</v-icon>
                  Basic Information
                </h3>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td class="font-weight-medium">Year</td>
                        <td>{{ vehicle.year || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Make</td>
                        <td>{{ vehicle.make || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Model</td>
                        <td>{{ vehicle.model || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Mileage</td>
                        <td>{{ formatMileage(vehicle.mileage) }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Exterior Color</td>
                        <td>{{ vehicle.exterior_color || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Interior Color</td>
                        <td>{{ vehicle.interior_color || 'N/A' }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>

              <!-- Location & Dealer Information -->
              <v-col cols="12" md="6">
                <h3 class="text-h6 font-weight-bold mb-3" :style="{ color: columnColor }">
                  <v-icon left :color="columnColor">mdi-map-marker</v-icon>
                  Location & Dealer
                </h3>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <td class="font-weight-medium">Location</td>
                        <td>{{ vehicle.location || 'N/A' }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-medium">Dealer</td>
                        <td>{{ vehicle.dealer_name || 'N/A' }}</td>
                      </tr>
                      <tr v-if="vehicle.is_external">
                        <td class="font-weight-medium">Source</td>
                        <td>{{ vehicle.site_name || vehicle.source || 'N/A' }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>

            <!-- Description -->
            <div v-if="vehicle.description" class="mt-4">
              <h3 class="text-h6 font-weight-bold mb-3" :style="{ color: columnColor }">
                <v-icon left :color="columnColor">mdi-text</v-icon>
                Description
              </h3>
              <p class="text-body-1">{{ vehicle.description }}</p>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Modal Actions -->
      <v-card-actions class="pa-4">
        <v-btn
          text
          color="grey"
          @click="closeModal"
          class="mr-2"
        >
          Back to Comparison
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="!vehicle.is_external"
          :color="columnColor"
          dark
          @click="viewFullDetails"
          class="mr-2"
        >
          <v-icon left>mdi-eye</v-icon>
          View Full Details
        </v-btn>
        <v-btn
          v-if="!vehicle.is_external && vehicle.dealer_name"
          :color="columnColor"
          outlined
          @click="contactSeller"
        >
          <v-icon left>mdi-phone</v-icon>
          Contact Seller
        </v-btn>
        <v-btn
          v-if="vehicle.is_external"
          :color="columnColor"
          dark
          @click="openExternalLink"
        >
          View on {{ vehicle.site_name || vehicle.source }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'VehicleDetailModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    vehicle: {
      type: Object,
      default: null
    },
    columnColor: {
      type: String,
      default: 'orange'
    }
  },
  data() {
    return {
      dialog: this.value
    };
  },
  watch: {
    value(newVal) {
      console.log('VehicleDetailModal: value changed to:', newVal);
      this.dialog = newVal;
    },
    dialog(newVal) {
      console.log('VehicleDetailModal: dialog changed to:', newVal);
      if (!newVal) {
        console.log('VehicleDetailModal: Emitting close events');
        this.$emit('input', false);
        this.$emit('close');
      }
    }
  },
  methods: {
    closeModal() {
      console.log('VehicleDetailModal: Closing modal');
      this.dialog = false;
      this.$emit('input', false);
      this.$emit('close');
    },

    getVehicleName() {
      if (!this.vehicle) return '';
      return this.vehicle.is_external && this.vehicle.title ? 
        this.vehicle.title : 
        `${this.vehicle.year} ${this.vehicle.make} ${this.vehicle.model}`;
    },

    getVehicleImage() {
      if (this.vehicle && this.vehicle.images && this.vehicle.images.length > 0) {
        return this.vehicle.images[0].image || require('@/assets/img/coming-soon.png');
      }
      return require('@/assets/img/coming-soon.png');
    },

    handleImageError(event) {
      event.target.src = require('@/assets/img/coming-soon.jpg');
    },

    currencyFormat(value) {
      if (!value) return '$0';
      const roundedValue = Math.round(value);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(roundedValue);
    },

    formatMileage(mileage) {
      if (!mileage || mileage === 'N/A') return 'N/A';
      if (typeof mileage === 'number') {
        return new Intl.NumberFormat('en-US').format(mileage) + ' mi';
      }
      return mileage;
    },

    getHeaderBackgroundColor() {
      console.log('VehicleDetailModal: Column color prop:', this.columnColor);
      const colorMap = {
        'orange': '#ff9800',
        'blue': '#2196f3',
        'green': '#4caf50'
      };
      const backgroundColor = colorMap[this.columnColor] || '#ff9800';
      console.log('VehicleDetailModal: Header background color:', backgroundColor);
      return backgroundColor;
    },

    viewFullDetails() {
      if (this.vehicle && !this.vehicle.is_external) {
        this.$router.push(`/view-inventory-details/${this.vehicle.id}`);
      }
    },

    contactSeller() {
      // Navigate to dealer contact or booking page
      if (this.vehicle && !this.vehicle.is_external) {
        // You can customize this to go to a specific dealer contact page
        this.$router.push(`/view-inventory-details/${this.vehicle.id}`);
      }
    },

    openExternalLink() {
      console.log('VehicleDetailModal: Opening external link for vehicle:', this.vehicle);

      if (!this.vehicle) {
        console.error('VehicleDetailModal: No vehicle data available');
        alert('Vehicle data is not available.');
        return;
      }

      if (this.vehicle.is_external) {
        // Try different possible URL properties
        const url = this.vehicle.source_url || this.vehicle.url || this.vehicle.link || this.vehicle.listing_url;

        if (url && url !== '#' && url !== '' && url !== 'N/A') {
          window.open(url, '_blank');
        } else {
          // Fallback: show message if no URL is available
          alert('Sorry, the external listing URL is not available for this vehicle.');
        }
      } else {
        alert('This is not an external vehicle.');
      }
    }
  }
};
</script>

<style scoped>
.vehicle-detail-container {
  max-height: 80vh;
  overflow-y: auto;
}

/* Vehicle header background color is set dynamically via :style binding */

.vehicle-images {
  background: #f5f5f5;
}

.vehicle-info {
  background: white;
}

/* Mobile responsive styles */
@media (max-width: 960px) {
  .vehicle-detail-container {
    max-height: 90vh;
  }

  .vehicle-header h2 {
    font-size: 1.5rem !important;
  }

  .vehicle-header .text-h3 {
    font-size: 1.8rem !important;
  }

  .v-carousel {
    height: 300px !important;
  }

  .vehicle-info .v-simple-table {
    font-size: 0.875rem;
  }
}

@media (max-width: 600px) {
  .vehicle-header {
    padding: 16px !important;
  }

  .vehicle-header h2 {
    font-size: 1.3rem !important;
  }

  .vehicle-header .text-h3 {
    font-size: 1.5rem !important;
  }

  .v-carousel {
    height: 250px !important;
  }

  .vehicle-info {
    padding: 16px !important;
  }

  .vehicle-info h3 {
    font-size: 1.1rem !important;
  }
}
</style>
